<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'

defineOptions({ inheritAttrs: false })

type CopyCommandState = 'idle' | 'copied' | 'failed'

const props = withDefaults(defineProps<{
  command: string
  idleLabel: string
  accessibleLabel?: string
  copiedLabel?: string
  failedLabel?: string
  resetAfterMs?: number
}>(), {
  accessibleLabel: '',
  copiedLabel: 'Скопировано',
  failedLabel: 'Не удалось скопировать',
  resetAfterMs: 2000,
})

const copyState = ref<CopyCommandState>('idle')
const currentLabel = computed(() => {
  if (copyState.value === 'copied') return props.copiedLabel
  if (copyState.value === 'failed') return props.failedLabel
  return props.idleLabel
})
const buttonAccessibleLabel = computed(() => (
  props.accessibleLabel.trim() ||
  `${props.idleLabel}: ${props.command}`
))
const announcement = computed(() => (
  copyState.value === 'idle' ? '' : currentLabel.value
))

let resetTimer: number | undefined
let copyRequestID = 0

watch(() => props.command, () => {
  copyRequestID += 1
  resetCopyState()
})
onBeforeUnmount(() => {
  copyRequestID += 1
  clearResetTimer()
})

async function copyCommand(): Promise<void> {
  const command = props.command
  if (!command) return
  const requestID = ++copyRequestID
  const copied = await copyTextToClipboard(command)
  if (requestID !== copyRequestID || command !== props.command) return
  copyState.value = copied ? 'copied' : 'failed'
  clearResetTimer()
  resetTimer = window.setTimeout(resetCopyState, props.resetAfterMs)
}

async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // Fall back for local/self-signed contexts where Clipboard API is blocked.
  }
  return copyTextFallback(text)
}

function copyTextFallback(text: string): boolean {
  const previousActiveElement = document.activeElement instanceof HTMLElement
    ? document.activeElement
    : null
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.left = '-9999px'
  textarea.style.top = '0'
  document.body.appendChild(textarea)
  textarea.focus()
  textarea.select()
  textarea.setSelectionRange(0, textarea.value.length)
  try {
    return document.execCommand('copy')
  } catch {
    return false
  } finally {
    document.body.removeChild(textarea)
    previousActiveElement?.focus({ preventScroll: true })
  }
}

function resetCopyState(): void {
  copyState.value = 'idle'
  clearResetTimer()
}

function clearResetTimer(): void {
  if (resetTimer === undefined) return
  window.clearTimeout(resetTimer)
  resetTimer = undefined
}
</script>

<template>
  <button
    v-bind="$attrs"
    type="button"
    class="copy-command-button"
    :class="copyState"
    :disabled="!command"
    :aria-label="buttonAccessibleLabel"
    @click="copyCommand"
  >
    <span class="copy-command-title">{{ currentLabel }}</span>
    <span class="copy-command-value">{{ command }}</span>
  </button>
  <span
    class="copy-command-announcement"
    role="status"
    aria-live="polite"
    aria-atomic="true"
  >{{ announcement }}</span>
</template>
